/**
 * Печатная форма вкладки «Голоса клиента» (#848) — для тех, кто в Aelo не
 * заходит. Зеркалит экран: строится из тех же строк вью-модели
 * (`voiceSections`), что и вкладка, поэтому второго источника правды у неё
 * нет. Оболочка, логотип и стили — общие с отчётом по записи.
 *
 * Весь подставляемый текст экранируется здесь: документ открывается через
 * `document.write` и наследует origin приложения, а формулировки — текст из
 * анализа разговора.
 */
import i18n from '@/i18n/config';
import { formatDateShort, formatDateTime } from '@/lib/utils';
import {
  isSmallSample,
  isThemeKind,
  itemDetail,
  itemText,
  rowCountsText,
  rowLabel,
  rowMark,
  rowMarkText,
  sectionCallsInWindow,
  sectionCapNotes,
  sectionEmptyKey,
  sectionZeroKey,
  truncationNote,
  type VoiceExportData,
  type VoiceSection,
} from '@/components/analytics/voice/voice-view-model';
import type { VoiceExportTheme } from '@/components/analytics/voice/voice-theme-filter-view-model';
import { themeBaselineText } from '@/components/analytics/voice/voice-themes-view-model';
import { renderDocument } from './shell';
import { AELO_LOCKUP_SVG } from './logo';
import { card, groupDivider } from './primitives';
import { escapeHtml } from './escape';
import { openPrintDocument } from './open-document';
import type { TFn } from './types';

export interface VoicePrintInput {
  /** Заголовок вкладки на языке смотрящего. */
  audienceLabel: string;
  sections: readonly VoiceSection[];
  data: VoiceExportData;
  /**
   * Lines the report opens with, when the tab's blocks are not counted over
   * the response's N (the questions blocks: coverage, data since, calls only).
   */
  notes?: readonly string[];
  /**
   * The theme filter in force: the header says whose
   * conversations these are, and without a comparable previous window the
   * report carries no trend and no previous-window note.
   */
  theme?: VoiceExportTheme;
}

/** The previous window is part of the report unless a theme filter has none. */
function showsPrevious(input: VoicePrintInput): boolean {
  return input.theme?.prevComparable !== false;
}

function renderSection(section: VoiceSection, input: VoicePrintInput, t: TFn): string {
  // A block read by its own request (the objection stream on Sales, #2350)
  // says its own cap: the report's opening lines speak for the others.
  const caveats = sectionCapNotes(section, showsPrevious(input), t)
    .map((line) => `<p class="lbl">${escapeHtml(line)}</p>`)
    .join('');
  // N = 0: not collected, as the screen says — not a list of zeros (#1379).
  if (sectionCallsInWindow(section, input.data) === 0) {
    return card({
      title: t(`voicePage.sections.${section.id}`),
      body: caveats + `<p class="lbl">${escapeHtml(t(sectionZeroKey(section)))}</p>`,
    });
  }
  // Collected, nothing named (no competitor this period): the screen's empty
  // line, not a bodiless card that reads as a missing section (code-review #1208).
  if (section.rows.length === 0) {
    return card({
      title: t(`voicePage.sections.${section.id}`),
      body: caveats + `<p class="lbl">${escapeHtml(t(sectionEmptyKey(section)))}</p>`,
    });
  }
  const rows = section.rows
    .map((row) => {
      // A theme whose previous window is not comparable names its base, as
      // its card on the screen does.
      const trendText = row.theme?.baseline
        ? themeBaselineText(row.theme.baseline, t, formatDateShort)
        : showsPrevious(input)
          ? rowMarkText(rowMark(row), t)
          : '';
      // Plural by the window total: "in 3 of 21 calls" declines with N, not K.
      const counts = rowCountsText(row, t);
      // A theme has no wording: its conversations carry a call category, which
      // the screen shows only when the row is opened.
      const wordings = (isThemeKind(row.kind) ? [] : row.items)
        .slice(0, 3)
        .map((item) => {
          const detail = itemDetail(row, item, t);
          return (
            `<li>${item.text ? `«${escapeHtml(item.text)}»` : escapeHtml(itemText(item, t))}` +
            `<span class="lbl"> ${escapeHtml(
              [detail, formatDateShort(item.createdAt)].filter(Boolean).join(' · ')
            )}</span></li>`
          );
        })
        .join('');
      const small = isSmallSample(row.calls)
        ? `<p class="lbl">${escapeHtml(t('voicePage.smallSample'))}</p>`
        : '';
      return (
        `<div class="blk"><div class="kv"><dt>${escapeHtml(rowLabel(row, t))}</dt>` +
        `<dd>${escapeHtml([counts, trendText].filter(Boolean).join(' · '))}</dd></div>` +
        small +
        (wordings ? `<ul>${wordings}</ul>` : '') +
        `</div>`
      );
    })
    .join('');
  return card({ title: t(`voicePage.sections.${section.id}`), body: caveats + rows });
}

/** Собрать документ — отдельно от открытия, чтобы его можно было проверить. */
export function renderVoicePrintDocument(
  input: VoicePrintInput,
  lang: string,
  t: TFn
): string {
  const { data } = input;
  const period = `${formatDateShort(data.periodStart)} – ${formatDateShort(data.periodEnd)}`;
  const themeLine = input.theme
    ? t('voicePage.themeFilter.printLine', { name: input.theme.name })
    : '';
  const header =
    `<div class="head"><div>${AELO_LOCKUP_SVG}<h1>${escapeHtml(t('voicePage.title'))}</h1></div>` +
    `<div class="head-meta">` +
    `<div class="brow">${escapeHtml(input.audienceLabel)}</div>` +
    `<div>${escapeHtml(period)}</div>` +
    (themeLine ? `<div>${escapeHtml(themeLine)}</div>` : '') +
    `<div>${escapeHtml(t('pdf.generated', { date: formatDateTime(new Date()) }))}</div>` +
    `</div></div>`;
  // The section's defaults read its own N; the questions export brings its
  // own lines (coverage, data since — `notes`).
  const notes = (
    input.notes ??
    (data.kind === 'questions'
      ? []
      : [
          t('voicePage.windowCalls', { count: data.callsInWindow }),
          t('voicePage.denominatorNote'),
          truncationNote(data, input.sections, t),
          data.prevTruncated && showsPrevious(input) ? t('voicePage.prevTruncated') : '',
          data.competitors.mergeUnavailable &&
          input.sections.some((section) => section.id === 'competitors')
            ? t('voicePage.competitors.mergeUnavailable')
            : '',
        ])
  )
    .filter(Boolean)
    .map((line) => `<p class="lbl">${escapeHtml(line)}</p>`)
    .join('');
  const body =
    groupDivider(input.audienceLabel) +
    notes +
    input.sections.map((section) => renderSection(section, input, t)).join('');
  return renderDocument({
    lang,
    title: escapeHtml(`${t('voicePage.title')} — ${input.audienceLabel}`),
    header,
    body,
    footer: `<div class="foot"><span>${escapeHtml(t('pdf.footer.brand'))}</span><span>${escapeHtml(period)}</span></div>`,
    printButtonLabel: escapeHtml(t('pdf.printButton')),
  });
}

/**
 * `fileBase` — the name the tab's CSVs carry (`voice-<tab>-<date>[-theme]`):
 * a report saved with pop-ups blocked is filed beside them, one per tab.
 */
export function openVoicePrintReport(input: VoicePrintInput, fileBase: string): void {
  const lang = i18n.language || 'en';
  // `dashboard` — ключи раздела, `common` — `pdf.*` оболочки.
  const t = i18n.getFixedT(lang, ['dashboard', 'common']) as TFn;
  openPrintDocument(renderVoicePrintDocument(input, lang, t), `${fileBase}.html`);
}
